/**
 * Auto Lossback Config Service
 * 
 * Service สำหรับจัดการระบบคืนยอดเสียอัตโนมัติ
 * เชื่อมต่อกับ Cloud Functions ใน lossback-config.js
 */

import { callCloudFunction } from '@/services/adapters/parse/parse-client'
import type {
  LossbackConfig,
  LossbackConfigQueryParams,
  CreateLossbackConfigData,
  UpdateLossbackConfigData,
  LossbackPreviewResult,
  LossbackExecuteResult,
  LossbackConfigStats,
} from './auto-lossback.types'

export class AutoLossbackService {
  /**
   * Map raw data to LossbackConfig interface
   */
  private static mapToConfig(raw: any): LossbackConfig {
    return {
      id: raw.id || raw.objectId,
      objectId: raw.id || raw.objectId,
      name: raw.name || '',
      description: raw.description || '',
      game_groups: raw.game_groups || [],
      percent_return: raw.percent_return || 0,
      amount_lose_min: raw.amount_lose_min || 0,
      after_bonus: raw.after_bonus ?? true,
      return_bonus: raw.return_bonus ?? false,
      return_bonus_turnover: raw.return_bonus_turnover || 1,
      period: raw.period || 'daily',
      auto_approve: raw.auto_approve ?? false,
      is_active: raw.is_active ?? false,
      run_count: raw.run_count || 0,
      last_run_at: raw.last_run_at ? new Date(raw.last_run_at) : undefined,
      last_run_result: raw.last_run_result,
      vip_only: raw.vip_only,
      member_group_ids: raw.member_group_ids,
      notify_member: raw.notify_member ?? true,
      createdAt: raw.createdAt || new Date().toISOString(),
      updatedAt: raw.updatedAt || new Date().toISOString(),
    }
  }

  // ============================================
  // CRUD Methods
  // ============================================

  /**
   * ดึง LossbackConfig ทั้งหมด
   */
  static async getLossbackConfigs(
    sessionToken: string,
    params: LossbackConfigQueryParams = {}
  ): Promise<{ items: LossbackConfig[]; total: number }> {
    try {
      const result = await callCloudFunction('getLossbackConfigs', {
        page: params.page || 1,
        limit: params.limit || 20,
        is_active: params.is_active,
        period: params.period,
      }, sessionToken)

      return {
        items: (result.data || []).map(this.mapToConfig),
        total: result.total || 0,
      }
    } catch (error) {
      console.error('Error getting lossback configs:', error)
      throw error
    }
  }

  /**
   * สร้าง Default LossbackConfigs
   */
  static async createDefaultConfigs(
    sessionToken: string
  ): Promise<{ created: string[]; skipped: string[] }> {
    try {
      const result = await callCloudFunction('createDefaultLossbackConfigs', {}, sessionToken)
      return {
        created: result.created || [],
        skipped: result.skipped || [],
      }
    } catch (error) {
      console.error('Error creating default configs:', error)
      throw error
    }
  }

  /**
   * สร้าง LossbackConfig ใหม่
   */
  static async createConfig(
    sessionToken: string,
    data: CreateLossbackConfigData
  ): Promise<LossbackConfig> {
    try {
      const result = await callCloudFunction('createLossbackConfig', data, sessionToken)
      return this.mapToConfig(result.data)
    } catch (error) {
      console.error('Error creating lossback config:', error)
      throw error
    }
  }

  /**
   * อัพเดท LossbackConfig
   */
  static async updateConfig(
    sessionToken: string,
    data: UpdateLossbackConfigData
  ): Promise<LossbackConfig> {
    try {
      const result = await callCloudFunction('updateLossbackConfig', data, sessionToken)
      return this.mapToConfig(result.data)
    } catch (error) {
      console.error('Error updating lossback config:', error)
      throw error
    }
  }

  /**
   * เปิด/ปิด LossbackConfig
   */
  static async toggleConfig(
    sessionToken: string,
    id: string,
    is_active?: boolean
  ): Promise<{ id: string; name: string; is_active: boolean }> {
    try {
      const result = await callCloudFunction('toggleLossbackConfig', {
        id,
        is_active,
      }, sessionToken)
      return result.data
    } catch (error) {
      console.error('Error toggling lossback config:', error)
      throw error
    }
  }

  /**
   * ลบ LossbackConfig (Soft Delete)
   */
  static async deleteConfig(
    sessionToken: string,
    id: string
  ): Promise<void> {
    try {
      await callCloudFunction('deleteLossbackConfig', { id }, sessionToken)
    } catch (error) {
      console.error('Error deleting lossback config:', error)
      throw error
    }
  }

  // ============================================
  // Execution Methods
  // ============================================

  /**
   * ทดสอบรัน LossbackConfig (ไม่โอนเงินจริง)
   */
  static async testConfig(
    sessionToken: string,
    id: string,
    date?: string
  ): Promise<LossbackPreviewResult> {
    try {
      const result = await callCloudFunction('testLossbackConfig', {
        id,
        date,
      }, sessionToken)
      return result.preview
    } catch (error) {
      console.error('Error testing lossback config:', error)
      throw error
    }
  }

  /**
   * รัน LossbackConfig ทันที (Manual Execute)
   */
  static async executeConfig(
    sessionToken: string,
    id: string,
    options: {
      date?: string
      auto_approve?: boolean
    } = {}
  ): Promise<LossbackExecuteResult> {
    try {
      const result = await callCloudFunction('executeLossbackConfig', {
        id,
        date: options.date,
        auto_approve: options.auto_approve,
      }, sessionToken)
      return result.result
    } catch (error) {
      console.error('Error executing lossback config:', error)
      throw error
    }
  }

  // ============================================
  // Stats Methods
  // ============================================

  /**
   * ดึงสถิติ LossbackConfig
   * (คำนวณจาก local data - ไม่มี cloud function แยก)
   */
  static async getStats(
    sessionToken: string
  ): Promise<LossbackConfigStats> {
    try {
      // ดึงข้อมูลทั้งหมดแล้วคำนวณ stats
      const result = await this.getLossbackConfigs(sessionToken, { limit: 1000 })
      const items = result.items

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return {
        total: items.length,
        active: items.filter(c => c.is_active).length,
        inactive: items.filter(c => !c.is_active).length,
        daily: items.filter(c => c.period === 'daily').length,
        weekly: items.filter(c => c.period === 'weekly').length,
        semi_monthly: items.filter(c => c.period === 'semi_monthly').length,
        monthly: items.filter(c => c.period === 'monthly').length,
        todayRuns: items.filter(c => {
          if (!c.last_run_at) return false
          const runDate = new Date(c.last_run_at)
          runDate.setHours(0, 0, 0, 0)
          return runDate.getTime() === today.getTime()
        }).length,
      }
    } catch (error) {
      console.error('Error getting lossback stats:', error)
      throw error
    }
  }
}
